import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";

type LinkItem = { name: string; path: string };
type Section = { title: string; links: LinkItem[] };

const sections: Section[] = [
  {
    title: "Main Pages",
    links: [
      { name: "Home", path: "/" },
      { name: "Why Son's", path: "/why-sons" },
      { name: "Cabanas", path: "/cabanas" },
      { name: "Tubing", path: "/tubing" },
      { name: "Property Map", path: "/property-map" },
      { name: "FAQs", path: "/faqs" },
      { name: "Contact", path: "/contact" },
      { name: "Specials", path: "/specials" },
      { name: "Group Bookings", path: "/group-bookings" },
    ],
  },
  {
    title: "Booking",
    links: [
      { name: "Book Now", path: "/book-now" },
      { name: "Son's Guadalupe — Day", path: "/book-now/sons-guadalupe" },
      { name: "Son's Island — Day", path: "/book-now/sons-island" },
      { name: "Son's Island — Overnight", path: "/book-now/sons-island-overnight" },
      { name: "River Ranch — Day", path: "/book-now/river-ranch" },
      { name: "River Ranch — Overnight", path: "/book-now/river-ranch-overnight" },
      { name: "Blue River Camp — Day", path: "/book-now/blue-river-camp" },
      { name: "Blue River Camp — Overnight", path: "/book-now/blue-river-camp-overnight" },
      { name: "Rio Cibolo — Day", path: "/book-now/rio-cibolo" },
      { name: "Rio Cibolo — Overnight", path: "/book-now/rio-cibolo-overnight" },
      { name: "Geronimo", path: "/book-now/geronimo" },
    ],
  },
  {
    title: "Local Guides",
    links: [
      { name: "River Road New Braunfels Cabins", path: "/river-road-new-braunfels-cabins" },
      { name: "Memorial Day Weekend New Braunfels", path: "/memorial-day-weekend-new-braunfels" },
      { name: "Family Getaways New Braunfels", path: "/family-getaways-new-braunfels" },
      { name: "Tent Camping New Braunfels", path: "/tent-camping-new-braunfels" },
      { name: "Private vs Public Guadalupe", path: "/private-vs-public-guadalupe" },
    ],
  },
  {
    title: "Blog",
    links: [
      { name: "Blog Home", path: "/blog" },
      { name: "Memorial Day Weekend on the Guadalupe", path: "/blog/memorial-day-weekend-guadalupe-river" },
      { name: "4th of July on the Guadalupe River", path: "/blog/4th-of-july-guadalupe-river" },
      { name: "River Road New Braunfels Guide", path: "/blog/river-road-new-braunfels-guide" },
      { name: "Best Time to Tube the Guadalupe", path: "/blog/best-time-to-tube-guadalupe-river" },
      { name: "Schlitterbahn vs Tubing the Guadalupe", path: "/blog/schlitterbahn-vs-tubing-guadalupe" },
      { name: "What to Pack for Tubing", path: "/blog/what-to-pack-tubing-guadalupe-river" },
      { name: "Guadalupe River Water Levels & Safety", path: "/blog/guadalupe-river-water-levels-safety" },
      { name: "Cabana Rentals — Price & What's Included", path: "/blog/cabana-rentals-price-whats-included" },
      { name: "Tubing with Kids on the Guadalupe", path: "/blog/tubing-with-kids-guadalupe-river" },
      { name: "Tube Rental Near New Braunfels", path: "/blog/guadalupe-river-tube-rental-near-new-braunfels" },
      { name: "Fun Things to Do with Kids Near New Braunfels", path: "/blog/fun-things-to-do-with-kids-near-new-braunfels" },
      { name: "Tubing the Guadalupe in New Braunfels", path: "/blog/tubing-guadalupe-river-new-braunfels" },
      { name: "Spring Break 2026 — Son's River Ranch", path: "/blog/spring-break-2026-sons-river-ranch" },
      { name: "Stress-Free Family Tubing on the Guadalupe", path: "/blog/stress-free-family-tubing-guadalupe" },
      { name: "River Road Parking Guide — New Braunfels", path: "/blog/river-road-parking-guide-new-braunfels" },
      { name: "Cabana vs Picnic Table — Which to Book", path: "/cabana-vs-picnic-table" },
    ],
  },
  {
    title: "Policies & Info",
    links: [
      { name: "Waivers", path: "/waivers" },
      { name: "Cancellation Policy", path: "/cancellation" },
      { name: "Privacy Policy", path: "/privacy" },
    ],
  },
];

const Sitemap = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Sitemap | Son's Guadalupe"
        description="Browse every page on Son's Guadalupe — cabanas, tubing, booking, local guides, blog, and policies."
        canonical="/sitemap"
      />
      <Header />
      <main className="pt-32 md:pt-36">
        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-lora text-4xl md:text-5xl font-bold text-teal mb-4">
                Sitemap
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every page on Son's Guadalupe, in one place.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sections.map((section) => (
                <div
                  key={section.title}
                  className="bg-card rounded-2xl p-6 shadow-card"
                >
                  <h2 className="font-heading text-xl font-bold text-foreground mb-4 pb-3 border-b border-border">
                    {section.title}
                  </h2>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          className="text-sm text-muted-foreground hover:text-teal transition-colors"
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center text-sm text-muted-foreground">
              Looking for the XML version?{" "}
              <a
                href="/sitemap.xml"
                className="text-teal hover:underline"
              >
                View sitemap.xml
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Sitemap;
